import { createRoute, useLoaderData } from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";
import { unwrapPageInitialData, type PageInitialData } from "~/app/api/pageData";
import { accountSettingsPageQueryOptions } from "~/app/api/pageData/accountSettingsPageQuery";
import type { AccountSettingsPageInitialData } from "~/app/api/pageData/accountSettingsPageInitialData";
import { StorefrontShellFrame } from "~/app/shell/StorefrontShellFrame";
import { useStorefrontShellProps } from "~/app/router/storefrontShellPropsContext";
import { rootRoute } from "~/app/router/rootRoute";
import { createAccountSettings } from "~/pages/AccountSettingsPage/AccountSettingsPage";

const ACCOUNT_SETTINGS_PATH = "/my-account/accountSettings";

/**
 * The {@code AccountSettingsPage} island is prop-less: it derives its entire view model from
 * runtime hooks ({@code userIsLoggedIn}, {@code useMediaQuery}, {@code getMyAccountTiles},
 * {@code getMyCompanyTiles}, {@code userCanSeeMyCompanySettings}). The legacy controller
 * assembled a large set of model attributes ({@code privacyPolicy}, {@code inputFieldValues},
 * option lists, {@code profileCenterLink}/{@code profileCenterEmailLink},
 * {@code accountSalesPartner}, {@code isSalesPartnerSaved}) via
 * {@code populateModelFromInitialData}, but none of them were ever read by the island — they
 * were dead glue left over from the pre-island JSP. The body still uses the shared typed
 * {@code MyAccountPageInitialData} wire contract, but no transformation is needed here.
 */
function AccountSettingsRoute() {
  const result = useLoaderData({
    from: ACCOUNT_SETTINGS_PATH,
  }) as PageInitialData<AccountSettingsPageInitialData>;
  const shellProps = useStorefrontShellProps();

  return (
    <StorefrontShellFrame variant={shellProps.variant} metadata={result.metadata}>
      {createAccountSettings()}
    </StorefrontShellFrame>
  );
}

export async function loadAccountSettingsPageData(context: { queryClient: QueryClient }) {
  const result = await context.queryClient.ensureQueryData(accountSettingsPageQueryOptions());

  return unwrapPageInitialData(result);
}

export const accountSettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ACCOUNT_SETTINGS_PATH,
  loader: async ({ context }) => loadAccountSettingsPageData(context),
  component: AccountSettingsRoute,
});
